import { defineMessages } from 'react-intl';

export const scope = 'app.components.FormatTemplate';

export const messages = defineMessages({
  // Shared
  loading: { id: `${scope}.loading`, defaultMessage: 'Loading…' },
  cancel: { id: `${scope}.cancel`, defaultMessage: 'Cancel' },
  recommended: { id: `${scope}.recommended`, defaultMessage: 'Recommended' },

  // Pills
  playerRange: { id: `${scope}.playerRange`, defaultMessage: '{min}–{max} {skaterWord}' },
  assessmentSessions: { id: `${scope}.assessmentSessions`, defaultMessage: '{count} {sessionWord}' },
  teams: { id: `${scope}.teams`, defaultMessage: '{count} {teamWord}' },

  // Template list
  noTemplates: { id: `${scope}.noTemplates`, defaultMessage: 'No format stage templates yet.' },
  noSystemTemplates: { id: `${scope}.noSystemTemplates`, defaultMessage: 'No global templates yet.' },
  preview: { id: `${scope}.preview`, defaultMessage: 'Preview' },
  edit: { id: `${scope}.edit`, defaultMessage: 'Edit' },
  delete: { id: `${scope}.delete`, defaultMessage: 'Delete' },
  globalToggleLabel: { id: `${scope}.globalToggleLabel`, defaultMessage: 'Global' },
  globalToggleError: {
    id: `${scope}.globalToggleError`,
    defaultMessage: 'Couldn\'t change who can see "{name}". Please try again.',
  },
  confirmPromoteTitle: { id: `${scope}.confirmPromoteTitle`, defaultMessage: 'Make this template global?' },
  confirmPromoteBody: {
    id: `${scope}.confirmPromoteBody`,
    defaultMessage: 'Every association will be able to apply "{name}". You can undo this later.',
  },
  confirmPromote: { id: `${scope}.confirmPromote`, defaultMessage: 'Make Global' },
  confirmDemoteTitle: { id: `${scope}.confirmDemoteTitle`, defaultMessage: 'Remove global access?' },
  confirmDemoteBody: {
    id: `${scope}.confirmDemoteBody`,
    defaultMessage: '"{name}" will go back to the association that created it. Other associations will no longer see it.',
  },
  confirmDemote: { id: `${scope}.confirmDemote`, defaultMessage: 'Remove Global' },
  globalPromoted: {
    id: `${scope}.globalPromoted`,
    defaultMessage: '"{name}" is now global — it moved to the global templates list on the Admin page.',
  },
  globalDemoted: {
    id: `${scope}.globalDemoted`,
    defaultMessage: '"{name}" is no longer global — it moved back to the templates list of the association that created it.',
  },
  confirmDeleteTitle: { id: `${scope}.confirmDeleteTitle`, defaultMessage: 'Delete template?' },
  confirmDeleteBody: {
    id: `${scope}.confirmDeleteBody`,
    defaultMessage: 'Delete "{name}"? This cannot be undone.',
  },
  deleteError: {
    id: `${scope}.deleteError`,
    defaultMessage: 'Couldn\'t delete "{name}". Please try again.',
  },

  // Use-template modal
  applyTitle: { id: `${scope}.applyTitle`, defaultMessage: 'Apply a Format Template' },
  loadingTemplates: { id: `${scope}.loadingTemplates`, defaultMessage: 'Loading templates…' },
  noTemplatesForAssociation: {
    id: `${scope}.noTemplatesForAssociation`,
    defaultMessage: 'No templates available for this association.',
  },
  ageGroupLabel: { id: `${scope}.ageGroupLabel`, defaultMessage: 'Age group' },
  templateLabel: { id: `${scope}.templateLabel`, defaultMessage: 'Template' },
  selectAgeGroupFirst: { id: `${scope}.selectAgeGroupFirst`, defaultMessage: 'Select an age group first' },
  noTemplatesForBucket: {
    id: `${scope}.noTemplatesForBucket`,
    defaultMessage: 'No templates for this age group yet.',
  },
  rosterCount: { id: `${scope}.rosterCount`, defaultMessage: 'Current roster: {count} {skaterWord}' },
  rosterCountWithGoalies: {
    id: `${scope}.rosterCountWithGoalies`,
    defaultMessage: 'Current roster: {nonGoalieCount} {skaterWord} · {goalieCount} {goalieWord}',
  },
  emptyTemplateWarning: {
    id: `${scope}.emptyTemplateWarning`,
    defaultMessage: 'This template has no stages yet, so there is nothing to apply.',
  },
  previewError: {
    id: `${scope}.previewError`,
    defaultMessage: 'Could not load this template\'s stages. Please try again.',
  },
  applyModeTitle: { id: `${scope}.applyModeTitle`, defaultMessage: 'Apply mode' },
  replaceOption: {
    id: `${scope}.replaceOption`,
    defaultMessage: 'Replace — remove all incomplete stages first, then add template stages',
  },
  appendOption: {
    id: `${scope}.appendOption`,
    defaultMessage: 'Append — add template stages after existing stages',
  },
  applying: { id: `${scope}.applying`, defaultMessage: 'Applying…' },
  applyTemplate: { id: `${scope}.applyTemplate`, defaultMessage: 'Apply Template' },
  selectAgeGroupStatus: { id: `${scope}.selectAgeGroupStatus`, defaultMessage: 'Select an age group first.' },
  applyError: { id: `${scope}.applyError`, defaultMessage: 'Could not apply the template. Please try again.' },

  // Save-template modal
  saveTitle: { id: `${scope}.saveTitle`, defaultMessage: 'Name This Template' },
  savingStages: {
    id: `${scope}.savingStages`,
    defaultMessage: 'Saving {count} selected {stageWord} as a reusable template.',
  },
  templateNameLabel: { id: `${scope}.templateNameLabel`, defaultMessage: 'Template name' },
  saving: { id: `${scope}.saving`, defaultMessage: 'Saving…' },
  saveTemplate: { id: `${scope}.saveTemplate`, defaultMessage: 'Save Template' },
  saveError: { id: `${scope}.saveError`, defaultMessage: 'Could not save template. Please try again.' },
  diagramPendingCreate: {
    id: `${scope}.diagramPendingCreate`,
    defaultMessage: 'Diagram ready — it uploads when you save.',
  },
  saveDiagramError: {
    id: `${scope}.saveDiagramError`,
    defaultMessage: 'The template was saved, but the diagram upload failed. Save again to retry the diagram, or add it later from the template list.',
  },

  // Edit-template modal
  editTitle: { id: `${scope}.editTitle`, defaultMessage: 'Edit Template' },
  recommendationsTitle: { id: `${scope}.recommendationsTitle`, defaultMessage: 'Recommendations' },
  stagesTitle: { id: `${scope}.stagesTitle`, defaultMessage: 'Stages' },
  stagesEditHint: {
    id: `${scope}.stagesEditHint`,
    defaultMessage: 'Save applies your template and stage edits together — edits still pending on a stage are included automatically.',
  },
  ageGroupsLabel: { id: `${scope}.ageGroupsLabel`, defaultMessage: 'Age groups' },
  minPlayersLabel: { id: `${scope}.minPlayersLabel`, defaultMessage: 'Min {skaterWord}' },
  maxPlayersLabel: { id: `${scope}.maxPlayersLabel`, defaultMessage: 'Max {skaterWord}' },
  teamOutcomesLabel: { id: `${scope}.teamOutcomesLabel`, defaultMessage: 'Team outcomes' },
  teamOutcomesHelp: {
    id: `${scope}.teamOutcomesHelp`,
    defaultMessage: 'Comma-separated, e.g. A-Team, B-Team',
  },
  save: { id: `${scope}.save`, defaultMessage: 'Save' },
  editError: { id: `${scope}.editError`, defaultMessage: 'Could not save changes. Please try again.' },

  // Catalog-editor modal
  editCatalogTitle: { id: `${scope}.editCatalogTitle`, defaultMessage: 'Edit Catalog Template' },
  catalogEditHint: {
    id: `${scope}.catalogEditHint`,
    defaultMessage: 'The catalog manages this template\'s structure — which stages run, on which day, over how many groups. Stage settings are editable here, and a stage\'s settings reach every player band that runs it.',
  },
  catalogStageSettingsTitle: { id: `${scope}.catalogStageSettingsTitle`, defaultMessage: 'Stage settings' },
  catalogStagesEditHint: {
    id: `${scope}.catalogStagesEditHint`,
    defaultMessage: 'Save applies each edited stage\'s settings to every band running that stage — edits still pending on a stage are included automatically.',
  },
  bandReach: {
    id: `${scope}.bandReach`,
    defaultMessage: 'Runs in {count} of {total} player {bandWord}:',
  },
  bandDiagramsTitle: { id: `${scope}.bandDiagramsTitle`, defaultMessage: 'Band diagrams' },
  bandDiagramsHelp: {
    id: `${scope}.bandDiagramsHelp`,
    defaultMessage: 'Each player band shows its own diagram. A new image replaces the band\'s diagram when you save.',
  },
  replaceDiagram: { id: `${scope}.replaceDiagram`, defaultMessage: 'Replace diagram' },
  addDiagram: { id: `${scope}.addDiagram`, defaultMessage: 'Add diagram' },
  uploadingDiagram: { id: `${scope}.uploadingDiagram`, defaultMessage: 'Uploading…' },
  diagramPending: { id: `${scope}.diagramPending`, defaultMessage: 'New diagram ready — it replaces this one when you save.' },
  noDiagram: { id: `${scope}.noDiagram`, defaultMessage: 'No diagram yet.' },
  diagramUploadError: {
    id: `${scope}.diagramUploadError`,
    defaultMessage: 'Could not upload the diagram. Please try again.',
  },
  catalogSaveError: {
    id: `${scope}.catalogSaveError`,
    defaultMessage: 'Could not save the catalog settings. Please try again.',
  },
  loadingStageSettings: { id: `${scope}.loadingStageSettings`, defaultMessage: 'Loading stage settings…' },

  // Custom-template diagram (edit modal)
  diagramTitle: { id: `${scope}.diagramTitle`, defaultMessage: 'Diagram' },

  // Preview modal
  stageCount: { id: `${scope}.stageCount`, defaultMessage: '{count} {stageWord}' },
  groups: { id: `${scope}.groups`, defaultMessage: '{count} {groupWord}' },
  bandsTitle: { id: `${scope}.bandsTitle`, defaultMessage: '{skaterWord} count bands' },
  bandsHelp: {
    id: `${scope}.bandsHelp`,
    defaultMessage: 'Stages are built from the band matching the age group\'s roster when this template is applied.',
  },
  noStages: { id: `${scope}.noStages`, defaultMessage: 'This template has no stages.' },
  loadingDetails: { id: `${scope}.loadingDetails`, defaultMessage: 'Loading template details…' },
  close: { id: `${scope}.close`, defaultMessage: 'Close' },

  // Validation — schemas source their yup message from each descriptor's defaultMessage;
  // FieldError renders the error through IntlMsg. See CLAUDE.md i18n convention.
  errNameRequired: { id: `${scope}.errNameRequired`, defaultMessage: 'Enter a template name' },
  errNumberInvalid: { id: `${scope}.errNumberInvalid`, defaultMessage: 'Enter a number' },
  errWholeNumbers: { id: `${scope}.errWholeNumbers`, defaultMessage: 'Whole numbers only' },
  errNotNegative: { id: `${scope}.errNotNegative`, defaultMessage: 'Cannot be negative' },
  errMaxGteMin: { id: `${scope}.errMaxGteMin`, defaultMessage: 'Max must be at least the minimum' },
  errSelectTemplate: { id: `${scope}.errSelectTemplate`, defaultMessage: 'Select a template to apply' },
  errPreviewNotReady: {
    id: `${scope}.errPreviewNotReady`,
    defaultMessage: 'Still checking this template — try again in a moment.',
  },
});
